/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Reservation} from "types/entities/Reservation";
import {createAction} from "helpers/createAction";
import BookingLoginAction, {ReservationsTableRequest} from "store/bookingLogin/BookingLoginAction";
import {
    SellerAssignSellerRequest,
    SellerDuplicateOfferRequest,
    SellerExtraInvoiceNoteToReservation,
    SellerExtraOfferNoteToReservation,
    SellerReservationRebookingRequest,
    SellerSetCustomerAsLeadRequest,
} from "types/entities/SellerReservations";
import ajax from "helpers/ajax";
import {DEFAULT_HALL_ID, SELLER_API_KEY} from "helpers/config";
import {consoleLog} from "utils/utility";
import {PackagesCart} from "types/entities/PackagesCart";
import {Company} from "types/entities/Company";
import {Driver} from "types/entities/Driver";
import SessionAction from "store/session/SessionAction";
import {ReservationCheckoutPlan} from "types/entities/ReservationCheckoutPlan";
import {ThunkAction} from "model/store/thunk";
import {PackageUpsellItem, ReservationNote} from "store/booking/BookingAction";
import {
    SellerReducerPushReservationNote,
    SellerReducerSetExtraInvoiceNoteToReservation,
    SellerReducerSetExtraOfferNoteToReservation,
    SellerReducerSetPackageCartToReservation,
} from "store/seller/SellerReducer";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PackageItemItemType} from "types/entities/PackageItem";
import {PackagesCartItemVoucher} from "types/entities/PackagesCartItemVoucher";
import {ReservationPlan} from "types/entities/ReservationPlan";

class SellerAction {
    static readonly SET_RESERVATION = "SellerAction.SetReservation";
    static readonly DELETE_RESERVATION = "SellerAction.DeleteReservation";
    static readonly SET_RESERVATIONS = "SellerAction.SetReservations";
    static readonly SET_TABLE_STATE = "SellerAction.SetTableState";
    static readonly CLEAR_RESERVATIONS = "SellerAction.ClearReservations";
    static readonly CLEAR_TABLE_STATE = "SellerAction.ClearTableState";
    static readonly SET_RESERVATION_CHECKOUT_PLANS = "SellerAction.SetReservationCheckoutPlans";
    static readonly PUSH_RESERVATION_NOTE = "SellerAction.PushReservationNote";
    static readonly SET_PACKAGE_CART_TO_RESERVATION = "SellerAction.SetPackageCartToReservation";
    static readonly SET_RESERVATION_PLANS = "SellerAction.SetReservationPlans";
    static readonly SET_EXTRA_INVOICE_NOTE_TO_RESERVATION = "SellerAction.SetExtraInvoiceNoteToReservation";
    static readonly SET_EXTRA_OFFER_NOTE_TO_RESERVATION = "SellerAction.SetExtraOfferNoteToReservation";

    static fetchGetSellerLoginWithPassword = (email: string, password: string): ((dispatch) => Promise<Driver>) => {
        return async dispatch => {
            try {
                const response: {
                    user: Driver;
                    reason?: string;
                    access_token: string;
                    token_type: string;
                    expires_at: number;
                    uid: number;
                    home_hall_id: number;
                    watchdogWorking: boolean;
                } = await ajax.post("/online-venue/reservation/seller/login-with-password", {
                    identifier: email,
                    password: password,
                    api_key: SELLER_API_KEY,
                });
                if (response.user) {
                    dispatch(SessionAction.setSession(response));
                    return Promise.resolve(response.user);
                } else {
                    consoleLog("fetchGetSellerLoginWithPassword - REJECT");
                    return Promise.reject(response.reason);
                }
            } catch (err) {
                return Promise.reject(err);
            }
        };
    };

    static fetchSetReservationRebooking = (data: SellerReservationRebookingRequest) => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;
            return ajax
                .post(
                    "/online-venue/reservation/seller/rebooking/" + data.reservation_id,
                    {
                        hall_id: DEFAULT_HALL_ID,
                        date: data?.date,
                        time: data?.time,
                        api_key: SELLER_API_KEY,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                )
                .then(
                    (onResolve: {reservation: Reservation}) => {
                        return Promise.resolve(onResolve);
                    },
                    onReject => {
                        consoleLog("Fetch data error:", onReject);
                        return Promise.reject(onReject);
                    }
                )
                .catch(error => {
                    if (error.name === "CanceledError") {
                        consoleLog("Request aborted:", error.message);
                    } else {
                        consoleLog("Request error:", error);
                    }
                    return Promise.reject(error);
                });
        };
    };

    static fetchAssignSellerToReservation = (data: SellerAssignSellerRequest) => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;
            return ajax
                .post(
                    "/online-venue/reservation/seller/assign-to-reservation/" + data.reservation_id,
                    {
                        hall_id: DEFAULT_HALL_ID,
                        seller_id: data.seller_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                )
                .then(
                    (onResolve: {reservation: Reservation}) => {
                        return Promise.resolve(onResolve);
                    },
                    onReject => {
                        consoleLog("Fetch data error:", onReject);
                        return Promise.reject(onReject);
                    }
                )
                .catch(error => {
                    if (error.name === "CanceledError") {
                        consoleLog("Request aborted:", error.message);
                    } else {
                        consoleLog("Request error:", error);
                    }
                    return Promise.reject(error);
                });
        };
    };

    static fetchDuplicateOffer = (data: SellerDuplicateOfferRequest) => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;
            return ajax
                .post(
                    "/online-venue/reservation/seller/duplicate/" + data.reservation_id,
                    {
                        hall_id: DEFAULT_HALL_ID,
                        option: data?.option,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                )
                .then(
                    (onResolve: {success: boolean}) => {
                        return Promise.resolve(onResolve);
                    },
                    onReject => {
                        consoleLog("Fetch data error:", onReject);
                        return Promise.reject(onReject);
                    }
                )
                .catch(error => {
                    if (error.name === "CanceledError") {
                        consoleLog("Request aborted:", error.message);
                    } else {
                        consoleLog("Request error:", error);
                    }
                    return Promise.reject(error);
                });
        };
    };

    static fetchSetCustomerAsLead = (data: SellerSetCustomerAsLeadRequest, packagesCart: PackagesCart) => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;
            return ajax
                .post("/booking/cart/" + packagesCart.id + "/" + packagesCart.token + "/set-customer-as-lead", data, {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                })
                .then(
                    (onResolve: {customer: Company}) => {
                        return Promise.resolve(onResolve);
                    },
                    onReject => {
                        consoleLog("Fetch data error:", onReject);
                        return Promise.reject(onReject);
                    }
                )
                .catch(error => {
                    if (error.name === "CanceledError") {
                        consoleLog("Request aborted:", error.message);
                    } else {
                        consoleLog("Request error:", error);
                    }
                    return Promise.reject(error);
                });
        };
    };

    static forgottenPassword = (email: string, app_url?: string): ThunkAction<Promise<Response>, any> => {
        return (dispatch): Promise<any> => {
            return ajax
                .post("/online-venue/reservation/seller/forgotten-password", {
                    email: email,
                    app_url: app_url,
                })
                .then(r => {
                    // dispatch(SessionAction.setSession(r));
                    return r;
                });
        };
    };

    static resetPassword = (email: string, access_token: string, new_password: string, app_id?: string): ThunkAction<Promise<Response>, any> => {
        return (dispatch): Promise<any> => {
            return ajax
                .post("/online-venue/reservation/seller/reset-password", {
                    email: email,
                    password_hash: access_token,
                    new_password: new_password,
                    app_id: app_id,
                })
                .then(r => {
                    // dispatch(SessionAction.setSession(r));
                    return r;
                });
        };
    };

    static fetchSetReservationCheckoutPlan = (reservation_id: number, checkout_plan: ReservationCheckoutPlan[]) => {
        return dispatch => {
            return ajax.post("/online-venue/reservation/seller/set-reservation-checkout-plan/" + reservation_id, {checkout_plan}).then(r => {
                return r;
            });
        };
    };

    static setUpsellsToReservationBySeller = (reservation_id: number, upsell: PackageUpsellItem[], seller_id: number): ThunkAction<Promise<any>> => {
        return async dispatch => {
            try {
                const response: {
                    packagesCart: PackagesCart;
                    reason?: string;
                } = await ajax.post("/online-venue/reservation/seller/add_upsells/" + reservation_id, {
                    upsell: upsell,
                    seller_id: seller_id,
                });
                if (response?.packagesCart) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart));

                    return Promise.resolve(response);
                } else {
                    consoleLog("setUpsellsToReservationBySeller - REJECT");
                    return Promise.reject(response.reason);
                }
            } catch (err) {
                return Promise.reject(err);
            }
        };
    };

    static fetchChangeUpsellPriceAndQuantity = (reservation_id: number, packagesCartItem: PackagesCartItem, type: string, value: string | number) => {
        return async dispatch => {
            const url =
                packagesCartItem.packageItem?.type === PackageItemItemType.Upsell
                    ? "/online-venue/reservation/seller/change_upsell/"
                    : "/online-venue/reservation/seller/change_item/";
            try {
                const response: {packagesCart: PackagesCart; success: boolean} = await ajax.post(url + reservation_id, {
                    item_id: packagesCartItem.id,
                    packages_cart_id: packagesCartItem.packages_cart_id,
                    package_item_id: packagesCartItem.package_item_id,
                    type: type,
                    value: value,
                    api_key: SELLER_API_KEY,
                });
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart));
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("upsell_change_failed");
                }
            } catch (err: any) {
                if (err && err?.status == 401) {
                    dispatch(SellerAction.fetchSellerLogout());
                    window.location.href = "/booking/seller/login";
                }
                return Promise.reject("upsell_change_failed");
            }
        };
    };

    static fetchSellerLogout = () => {
        return async dispatch => {
            dispatch(BookingLoginAction.clearReservations());
            return dispatch(SessionAction.logout());
        };
    };

    static fetchChangeVoucherPrice = (reservation_id: number, packagesCartItemVoucher: PackagesCartItemVoucher, type: string, value: string | number) => {
        return async dispatch => {
            const url = "/online-venue/reservation/seller/change_voucher/";
            try {
                const response: {packagesCart: PackagesCart; success: boolean} = await ajax.post(url + reservation_id, {
                    packages_cart_item_voucher_id: packagesCartItemVoucher.id,
                    packages_cart_id: packagesCartItemVoucher.packages_cart_id,
                    type: type,
                    value: value,
                    api_key: SELLER_API_KEY,
                });
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart));
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("voucher_change_failed");
                }
            } catch (err) {
                consoleLog("voucher_change_failed - reject");
                consoleLog(err);
                return Promise.reject("voucher_change_failed");
            }
        };
    };

    static fetchSellerAddVoucher = (reservation_id: number, type: string, value: string | number) => {
        return async (dispatch, getState) => {
            const url = "/online-venue/reservation/seller/add_voucher/";
            const session = getState().sessionState.session;

            try {
                const response: {
                    success: boolean;
                    packagesCart: PackagesCart;
                } = await ajax.post(
                    url + reservation_id,
                    {
                        type: type,
                        value: value,
                        api_key: SELLER_API_KEY,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart));

                    return Promise.resolve(response);
                } else {
                    return Promise.reject("voucher_change_failed");
                }
            } catch (err) {
                consoleLog("voucher_change_failed - reject");
                consoleLog(err);
                return Promise.reject("voucher_change_failed");
            }
        };
    };

    static fetchDeleteUpsell = (reservation_id: number, packagesCartItem: PackagesCartItem) => {
        return async dispatch => {
            try {
                const response: {
                    success: boolean;
                    packagesCart: PackagesCart;
                } = await ajax.delete("/online-venue/reservation/seller/delete_upsell/" + reservation_id, {
                    data: {
                        item_id: packagesCartItem.id,
                        packages_cart_id: packagesCartItem.packages_cart_id,
                        package_item_id: packagesCartItem.package_item_id,
                        api_key: SELLER_API_KEY,
                    },
                });
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart));
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("upsell_delete_failed");
                }
            } catch (err) {
                consoleLog("upsell_delete_failed - reject");
                consoleLog(err);
                return Promise.reject("upsell_delete_failed");
            }
        };
    };

    static fetchDeleteVoucher = (reservation_id: number, voucher_id: number) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    success: boolean;
                    packagesCart: PackagesCart;
                } = await ajax.delete("/online-venue/reservation/seller/delete_voucher/" + reservation_id, {
                    data: {
                        voucher_id: voucher_id,
                        api_key: SELLER_API_KEY,
                    },
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });
                if (response?.success) {
                    dispatch(SellerAction.setPackageCartToReservation(response.packagesCart));
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("voucher_delete_failed");
                }
            } catch (err) {
                consoleLog("voucher_delete_failed - reject");
                consoleLog(err);
                return Promise.reject("voucher_delete_failed");
            }
        };
    };

    static fetchSendSellerSecurityCodeForLogin = (email: string): ((dispatch) => Promise<Driver>) => {
        return async dispatch => {
            try {
                const response = await ajax.post("/online-venue/reservation/seller/send-login-with-code", {
                    identifier: email,
                    api_key: SELLER_API_KEY,
                });
                if (response?.success) {
                    return Promise.resolve(response);
                } else {
                    return Promise.reject("login_not_found");
                }
            } catch (err) {
                consoleLog("fetchSendSecurityCodeForLogin - reject");
                consoleLog(err);
                return Promise.reject("login_not_found");
            }
        };
    };

    static fetchGetCompanyFromAutocomplete = (name: string): ((dispatch) => Promise<Company[]>) => {
        return async dispatch => {
            try {
                const response = await ajax.post("/online-venue/reservation/seller/company-autocomplete", {
                    identifier: name,
                    api_key: SELLER_API_KEY,
                });
                if (response?.success) {
                    return Promise.resolve(response.companies);
                } else {
                    return Promise.reject(response);
                }
            } catch (err) {
                consoleLog("fetchGetCompanyFromAutocomplete - reject");
                consoleLog(err);
                return Promise.reject(err);
            }
        };
    };

    static fetchSetReservationPlan = (reservation_id: number, plan: ReservationPlan[]): ThunkAction<Promise<ReservationPlan[]>> => {
        return async (dispatch, getState): Promise<ReservationPlan[]> => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservation_plan: ReservationPlan[];
                } = await ajax.post(
                    "/admin/reservations/set-reservation-plan/" + reservation_id,
                    {
                        plan,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );
                if (response?.reservation_plan) {
                    dispatch(SellerAction.setReservationPlans(reservation_id, response.reservation_plan));
                    return Promise.resolve(response.reservation_plan);
                } else {
                    return Promise.reject(response);
                }
            } catch (err) {
                consoleLog("fetchSetReservationPlan - reject");
                consoleLog(err);
                return Promise.reject(err);
            }
        };
    };

    static fetchGetReservationPlan = (reservation_id: number): ThunkAction<Promise<string>> => {
        return async (dispatch, getState): Promise<string> => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    plan: string;
                } = await ajax.post(
                    "/online-venue/reservation/seller/get_reservation_plan/" + reservation_id,
                    {},
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );
                if (response?.plan) {
                    return Promise.resolve(response.plan);
                } else {
                    return Promise.reject(response);
                }
            } catch (err) {
                consoleLog("fetchGetReservationPlan - reject");
                consoleLog(err);
                return Promise.reject(err);
            }
        };
    };

    static fetchSetInvoiceNoteToReservation = (reservation_id: number, invoice_note: string | null) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservation: Reservation;
                } = await ajax.post(
                    "/admin/reservations/set-invoice-note/" + reservation_id,
                    {
                        invoice_note: invoice_note,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response?.reservation) {
                    return Promise.resolve(response);
                } else {
                    consoleLog("fetchSetInvoiceNoteToReservation - reject", response);
                    return Promise.reject(response);
                }
            } catch (err) {
                consoleLog("fetchSetInvoiceNoteToReservation - error", err);
                return Promise.reject(err);
            }
        };
    };

    static fetchSetOfferNoteToReservation = (reservation_id: number, offer_note: string | null) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservation: Reservation;
                } = await ajax.post(
                    "/admin/reservations/set-offer-note/" + reservation_id,
                    {
                        offer_note: offer_note,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response?.reservation) {
                    return Promise.resolve(response);
                } else {
                    consoleLog("fetchSetOfferNoteToReservation - reject", response);
                    return Promise.reject(response);
                }
            } catch (err) {
                consoleLog("fetchSetOfferNoteToReservation - error", err);
                return Promise.reject(err);
            }
        };
    };

    static setReservations = (data: Reservation[]) => {
        return createAction(SellerAction.SET_RESERVATIONS, data);
    };

    static setTableState = (payload: ReservationsTableRequest) => {
        return createAction(SellerAction.SET_TABLE_STATE, payload);
    };

    static setReservation = (reservation: Reservation) => {
        return createAction(SellerAction.SET_RESERVATION, reservation);
    };

    static deleteReservation = (reservation: Reservation) => {
        return createAction(SellerAction.DELETE_RESERVATION, reservation);
    };

    static clearReservations = () => {
        return createAction(SellerAction.CLEAR_RESERVATIONS, []);
    };

    static clearTableState = () => {
        return createAction(SellerAction.CLEAR_TABLE_STATE, null);
    };

    static setReservationCheckoutPlans(checkoutPlans: ReservationCheckoutPlan[]) {
        return createAction(SellerAction.SET_RESERVATION_CHECKOUT_PLANS, checkoutPlans);
    }

    static pushReservationNote = (note: ReservationNote): SellerReducerPushReservationNote => {
        return createAction(SellerAction.PUSH_RESERVATION_NOTE, note);
    };

    static setPackageCartToReservation = (data: PackagesCart): SellerReducerSetPackageCartToReservation => {
        return createAction(SellerAction.SET_PACKAGE_CART_TO_RESERVATION, data);
    };

    static setReservationPlans(reservation_id: number, plans: ReservationPlan[]) {
        return createAction(SellerAction.SET_RESERVATION_PLANS, {reservation_id: reservation_id, plans: plans});
    }

    static setExtraInvoiceNoteToReservation = (data: SellerExtraInvoiceNoteToReservation): SellerReducerSetExtraInvoiceNoteToReservation => {
        return createAction(SellerAction.SET_EXTRA_INVOICE_NOTE_TO_RESERVATION, data);
    };

    static setExtraOfferNoteToReservation = (data: SellerExtraOfferNoteToReservation): SellerReducerSetExtraOfferNoteToReservation => {
        return createAction(SellerAction.SET_EXTRA_OFFER_NOTE_TO_RESERVATION, data);
    };
}

export default SellerAction;
